import { useState } from 'react';
import { AlertTriangle, BarChart2, ShieldAlert, TrendingDown, TrendingUp, Users } from 'lucide-react';
import { useLeadershipReport } from '../../api/reports';
import client from '../../api/client';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { DashboardSkeleton } from '@/components/ui/skeleton';
import ErrorState from '../../components/ui/ErrorState';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatDate, weekLabel } from '../../lib/dates';

async function downloadPdf(weekOf?: string) {
  const response = await client.get('/reports/leadership/pdf', {
    params: weekOf ? { weekOf } : undefined,
    responseType: 'blob',
    transformResponse: [(data) => data],
  });
  const blob = new Blob([response.data as BlobPart], { type: 'application/pdf' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = weekOf ? `leadership-report-${weekOf}.pdf` : 'leadership-report.pdf';
  a.click();
  URL.revokeObjectURL(url);
}

export default function LeadershipReportPage() {
  const [weekOf, setWeekOf]         = useState('');
  const [downloading, setDownloading] = useState(false);
  const { data: report, isLoading, error, refetch } = useLeadershipReport(weekOf || undefined);

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Leadership report"
        actions={
          <div className="flex items-center gap-2">
            <input
              type="date" value={weekOf} onChange={(e) => setWeekOf(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
            <Button
              variant="secondary" size="sm" loading={downloading}
              onClick={async () => {
                setDownloading(true);
                try { await downloadPdf(weekOf || undefined); }
                finally { setDownloading(false); }
              }}
            >
              Download PDF
            </Button>
          </div>
        }
      />

      {isLoading && <DashboardSkeleton />}
      {error     && <ErrorState onRetry={refetch} />}

      {report && (
        <div className="space-y-6 print:space-y-8">
          {/* Summary stat cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Card className="transition-shadow hover:shadow-md">
              <CardContent className="p-5">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                  <BarChart2 className="h-5 w-5 text-primary" />
                </div>
                <p className="text-3xl font-bold text-foreground">{report.totalDeliveredPoints}</p>
                <p className="mt-1 text-sm font-medium text-muted-foreground">Points delivered</p>
                <p className="mt-0.5 text-xs text-muted-foreground/70">{weekLabel(report.weekOf)}</p>
              </CardContent>
            </Card>
            <Card className="transition-shadow hover:shadow-md">
              <CardContent className="p-5">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
                  <BarChart2 className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-3xl font-bold text-muted-foreground">{report.previousWeekPoints}</p>
                <p className="mt-1 text-sm font-medium text-muted-foreground">Points delivered</p>
                <p className="mt-0.5 text-xs text-muted-foreground/70">Previous week</p>
              </CardContent>
            </Card>
            {report.totalDeliveredPoints > 0 && report.previousWeekPoints > 0 && (() => {
              const delta = report.totalDeliveredPoints - report.previousWeekPoints;
              const pct   = Math.round((delta / report.previousWeekPoints) * 100);
              const up    = delta >= 0;
              return (
                <Card className={cn('transition-shadow hover:shadow-md', up ? 'border-emerald-200 dark:border-emerald-900/40' : 'border-red-200 dark:border-red-900/40')}>
                  <CardContent className="p-5">
                    <div className={cn('mb-4 flex h-10 w-10 items-center justify-center rounded-xl', up ? 'bg-emerald-50 dark:bg-emerald-950/40' : 'bg-red-50 dark:bg-red-950/40')}>
                      {up
                        ? <TrendingUp  className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                        : <TrendingDown className="h-5 w-5 text-red-500" />
                      }
                    </div>
                    <p className={cn('text-3xl font-bold', up ? 'text-emerald-600' : 'text-destructive')}>
                      {up ? '+' : ''}{pct}%
                    </p>
                    <p className="mt-1 text-sm font-medium text-muted-foreground">Week-on-week</p>
                    <p className="mt-0.5 text-xs text-muted-foreground/70">vs last week</p>
                  </CardContent>
                </Card>
              );
            })()}
          </div>

          {/* Team capacity */}
          <section>
            <div className="mb-3 flex items-center gap-2.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                <Users className="h-4 w-4 text-primary" />
              </div>
              <h2 className="text-sm font-semibold text-foreground">Team capacity</h2>
            </div>
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Engineer</TableHead>
                    <TableHead className="text-right">Active tasks</TableHead>
                    <TableHead className="text-right">Points</TableHead>
                    <TableHead className="text-right">Baseline</TableHead>
                    <TableHead className="text-right">Check-ins</TableHead>
                    <TableHead className="text-right">Blockers</TableHead>
                    <TableHead>Flag</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.engineers.map((eng) => (
                    <TableRow key={eng.engineerId}>
                      <TableCell className="font-medium text-foreground">{eng.name}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{eng.activeTasks}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{eng.totalPoints}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{eng.baselinePoints}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{eng.checkInsThisWeek}/5</TableCell>
                      <TableCell className="text-right text-muted-foreground">{eng.blockers}</TableCell>
                      <TableCell>{eng.isOverworked && <Badge label="overworked" variant="red" />}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </section>

          {/* At-risk tasks */}
          {report.escalations.length > 0 && (
            <section>
              <div className="mb-3 flex items-center gap-2.5">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-amber-50 dark:bg-amber-950/40">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                </div>
                <h2 className="text-sm font-semibold text-foreground">At-risk tasks</h2>
              </div>
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Task</TableHead>
                      <TableHead>Due</TableHead>
                      <TableHead>Level</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.escalations.map((esc) => (
                      <TableRow key={esc.taskId}>
                        <TableCell className="text-foreground">{esc.title}</TableCell>
                        <TableCell className="text-muted-foreground">{formatDate(esc.dueDate)}</TableCell>
                        <TableCell>
                          <Badge
                            label={esc.level === 'Overdue' ? 'overdue' : esc.level === 'TMinus1' ? 'T-1' : 'T-3'}
                            variant={esc.level === 'Overdue' ? 'red' : esc.level === 'TMinus1' ? 'red' : 'yellow'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </section>
          )}

          {/* Active blockers */}
          {report.blockers.length > 0 && (
            <section>
              <div className="mb-3 flex items-center gap-2.5">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-red-50 dark:bg-red-950/40">
                  <ShieldAlert className="h-4 w-4 text-red-500" />
                </div>
                <h2 className="text-sm font-semibold text-foreground">Active blockers</h2>
              </div>
              <div className="space-y-2">
                {report.blockers.map((b) => (
                  <Card key={b.taskId} className="border-red-200 bg-red-50">
                    <CardContent className="p-4">
                      <p className="text-sm font-medium text-red-800">{b.title}</p>
                      <p className="mt-0.5 text-xs text-red-600">{b.reason}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}

          <div className="flex items-center gap-3 print:hidden">
            <button
              onClick={() => window.print()}
              className="rounded-md border border-input px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
            >
              Print / Save as PDF
            </button>
          </div>
          <p className="hidden text-xs text-muted-foreground print:block">
            Generated from Cadence on {new Date().toLocaleDateString('en-GB')}
          </p>
        </div>
      )}
    </div>
  );
}
